import React, { useEffect, useState, useCallback } from "react";
import PLAN_URL from "../../../utils/PLAN_URL";
import axios from "axios";
import { useParams } from "react-router-dom";
import {
  Alert,
  Button,
  CircularProgress,
  Snackbar,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import EditIcon from "@mui/icons-material/Edit";
import * as S from "./styles";

function TempStock() {
  const { group_code, project_code } = useParams();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const openSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // 탭: 0 = 계획추가, 1 = 재고관리
  const [tabValue, setTabValue] = useState(0);
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  /********************************
   * 탭 0: 계획추가 (temp_order 관리)
   ********************************/
  const [allItems, setAllItems] = useState([]);
  const [tempOrders, setTempOrders] = useState([]);
  const [orderLoading, setOrderLoading] = useState(false);

  const [selectedItemId, setSelectedItemId] = useState("ALL"); // 기본: 전체 보기
  const [orderQty, setOrderQty] = useState("");
  const [orderDate, setOrderDate] = useState("");
  const [editingOrderId, setEditingOrderId] = useState(null);

  const fetchAllItemsAndOrders = useCallback(async () => {
    try {
      setOrderLoading(true);
      const [itemsRes, tempOrderRes] = await Promise.all([
        axios.get(
          PLAN_URL +
            `/api/item/getAllItems?group_code=${group_code}&project_code=${project_code}`
        ),
        axios.get(
          PLAN_URL +
            `/api/stock/getTempOrder?group_code=${group_code}&project_code=${project_code}`
        ),
      ]);

      if (Array.isArray(itemsRes.data)) {
        setAllItems(itemsRes.data);
      } else {
        console.error("Invalid allItems format:", itemsRes.data);
        openSnackbar("품목 정보 형식이 올바르지 않습니다.", "error");
      }

      if (Array.isArray(tempOrderRes.data)) {
        setTempOrders(tempOrderRes.data);
      } else {
        console.error("Invalid tempOrder format:", tempOrderRes.data);
        openSnackbar("임시 계획 정보 형식이 올바르지 않습니다.", "error");
      }
    } catch (error) {
      console.error("Error fetching items or temp orders:", error);
      openSnackbar(
        "계획추가 데이터를 불러오는 중 오류가 발생했습니다.",
        "error"
      );
    } finally {
      setOrderLoading(false);
    }
  }, [group_code, project_code]);

  useEffect(() => {
    if (tabValue === 0) {
      fetchAllItemsAndOrders();
    }
  }, [tabValue, fetchAllItemsAndOrders]);

  // 선택된 품목 기준 필터, 'ALL'이면 전체 보기
  const matchedOrders =
    selectedItemId === "ALL"
      ? tempOrders
      : tempOrders.filter((order) => order.item_id === Number(selectedItemId));

  const getItemCodeById = (item_id) => {
    const item = allItems.find((i) => i.item_id === item_id);
    return item?.item_code || "";
  };

  const clearOrderInputs = () => {
    setOrderQty("");
    setOrderDate("");
    setEditingOrderId(null);
  };

  const handleAddOrder = async () => {
    if (
      !selectedItemId ||
      selectedItemId === "ALL" ||
      !orderQty ||
      !orderDate
    ) {
      openSnackbar("품목(개별), 수량, 날짜를 모두 입력해주세요.", "error");
      return;
    }

    try {
      await axios.post(PLAN_URL + `/api/stock/postTempOrder`, {
        group_code,
        project_code,
        info: {
          item_id: Number(selectedItemId),
          t_date: orderDate,
          qty: Number(orderQty),
        },
      });
      openSnackbar("임시 계획이 추가되었습니다.");
      clearOrderInputs();
      fetchAllItemsAndOrders();
    } catch (error) {
      console.error("Error postTempOrder:", error);
      openSnackbar("임시 계획 추가 중 오류가 발생했습니다.", "error");
    }
  };

  const handleClickEditOrder = (order) => {
    setEditingOrderId(order.t_order_id);
    setSelectedItemId(String(order.item_id));
    setOrderQty(String(order.qty ?? ""));
    setOrderDate(order.t_date ?? "");
  };

  const handleUpdateOrder = async () => {
    if (
      !editingOrderId ||
      !selectedItemId ||
      selectedItemId === "ALL" ||
      !orderQty ||
      !orderDate
    ) {
      openSnackbar(
        "수정할 계획과 값(개별 품목, 수량, 날짜)을 모두 설정해주세요.",
        "error"
      );
      return;
    }

    try {
      await axios.put(PLAN_URL + `/api/stock/putTempOrder`, {
        group_code,
        project_code,
        info: {
          t_order_id: editingOrderId,
          item_id: Number(selectedItemId),
          t_date: orderDate,
          qty: Number(orderQty),
        },
      });
      openSnackbar("임시 계획이 수정되었습니다.");
      clearOrderInputs();
      fetchAllItemsAndOrders();
    } catch (error) {
      console.error("Error putTempOrder:", error);
      openSnackbar("임시 계획 수정 중 오류가 발생했습니다.", "error");
    }
  };

  const handleDeleteOrder = async (t_order_id) => {
    try {
      await axios.delete(
        PLAN_URL +
          `/api/stock/deleteTempOrder?group_code=${group_code}&project_code=${project_code}&order_id=${t_order_id}`
      );
      openSnackbar("임시 계획이 삭제되었습니다.");
      if (editingOrderId === t_order_id) {
        clearOrderInputs();
      }
      fetchAllItemsAndOrders();
    } catch (error) {
      console.error("Error deleteTempOrder:", error);
      openSnackbar("임시 계획 삭제 중 오류가 발생했습니다.", "error");
    }
  };

  /********************************
   * 탭 1: 재고관리 (기존 TempStock)
   ********************************/
  const [stockData, setStockData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [itemTypeFilter, setItemTypeFilter] = useState(1);

  const fetchStockData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        PLAN_URL +
          `/api/stock/getTempStock?group_code=${group_code}&project_code=${project_code}`
      );

      if (res.data && Array.isArray(res.data)) {
        setStockData(res.data);
        setOriginalData(JSON.parse(JSON.stringify(res.data)));
      } else {
        console.error("Invalid data format:", res.data);
        openSnackbar("데이터 형식이 올바르지 않습니다.", "error");
      }
    } catch (error) {
      console.error("Error fetching stock data:", error);
      openSnackbar("재고 데이터를 불러오는 중 오류가 발생했습니다.", "error");
    } finally {
      setLoading(false);
    }
  }, [group_code, project_code]);

  useEffect(() => {
    if (tabValue === 1) {
      fetchStockData();
    }
  }, [tabValue, fetchStockData]);

  const handleToggleEditMode = () => {
    if (editMode) {
      setStockData(JSON.parse(JSON.stringify(originalData)));
    } else {
      setOriginalData(JSON.parse(JSON.stringify(stockData)));
    }
    setEditMode(!editMode);
  };

  const handleValueChange = (index, field, value) => {
    let processedValue = value;
    if (field === "stock_quantity" || field === "safety_stock") {
      processedValue = Math.floor(parseFloat(value) || 0);
    }
    setStockData((prevData) =>
      prevData.map((item, i) =>
        i === index ? { ...item, [field]: processedValue } : item
      )
    );
  };

  const isValidStockValues = () => {
    return stockData.every((item) => {
      const stockQty = item.stock_quantity;
      const safetyStock = item.safety_stock;
      return (
        stockQty !== null &&
        stockQty !== undefined &&
        Number.isInteger(stockQty) &&
        stockQty >= 0 &&
        safetyStock !== null &&
        safetyStock !== undefined &&
        Number.isInteger(safetyStock) &&
        safetyStock >= 0
      );
    });
  };

  const handleSave = async () => {
    if (!isValidStockValues()) {
      openSnackbar("재고 수량과 안전 재고는 정수만 입력 가능합니다.", "error");
      return;
    }

    try {
      setSaving(true);
      const res = await axios.put(PLAN_URL + "/api/stock/updateTempStock", {
        group_code,
        project_code,
        info: stockData.map((item) => ({
          item_id: item.item_id,
          stock_quantity: item.stock_quantity,
          safety_stock: item.safety_stock,
        })),
      });

      if (res.status === 200) {
        openSnackbar("재고 정보가 성공적으로 저장되었습니다.", "success");
        setOriginalData(JSON.parse(JSON.stringify(stockData)));
        setEditMode(false);
      }
    } catch (error) {
      console.error("Error saving stock data:", error);
      openSnackbar("재고 정보 저장 중 오류가 발생했습니다.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setStockData(JSON.parse(JSON.stringify(originalData)));
    setEditMode(false);
    openSnackbar("변경사항이 취소되었습니다.", "info");
  };

  const isDataChanged = () => {
    if (!originalData.length || !stockData.length) return false;
    if (originalData.length !== stockData.length) return true;

    return stockData.some((item, index) => {
      const original = originalData[index];
      return (
        item.stock_quantity !== original.stock_quantity ||
        item.safety_stock !== original.safety_stock
      );
    });
  };

  const getItemTypeLabel = (itemType) => {
    return itemType === 0 ? "반제품" : itemType === 1 ? "완제품" : "-";
  };

  const handleItemTypeFilterChange = (event, newFilter) => {
    if (newFilter !== null) {
      setItemTypeFilter(newFilter);
    }
  };

  const filteredStockData = stockData.filter(
    (item) => item.item_type === itemTypeFilter
  );

  const stockInfoTestFunc = async () => {
    try {
      const res = await axios.get(
        PLAN_URL +
          `/api/item/generateItemStockInfoTest?group_code=${group_code}&project_code=${project_code}`
      );
      if (res.status === 200) {
        openSnackbar(res.data.message);
      } else {
        openSnackbar(res.data.message, "error");
      }
    } catch (e) {
      console.log(e);
      openSnackbar("오류가 발생하였습니다.", "error");
    }
  };

  return (
    <S.PageContainer>
      <S.HeaderContainer>
        <S.Title
          variant="h5"
          fontWeight="bold"
          sx={{
            display: "flex",
            alignItems: "center",
            fontSize: "22px !important",
          }}
        >
          임시 재고 / 계획 관리
        </S.Title>
        <S.TopBtnBox>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="temp tabs"
          >
            <Tab label="계획추가" />
            <Tab label="재고관리" />
          </Tabs>
        </S.TopBtnBox>
      </S.HeaderContainer>

      {/* 계획추가 탭 */}
      {tabValue === 0 && (
        <S.ContentContainer>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "center",
              }}
            >
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel id="plan-item-select-label">품목 선택</InputLabel>
                <Select
                  labelId="plan-item-select-label"
                  label="품목 선택"
                  value={selectedItemId}
                  onChange={(e) => setSelectedItemId(e.target.value)}
                >
                  <MenuItem value="ALL">
                    <em>전체 보기</em>
                  </MenuItem>
                  {allItems.map((item) => (
                    <MenuItem key={item.item_id} value={item.item_id}>
                      {item.item_code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="수량 (qty)"
                size="small"
                type="number"
                value={orderQty}
                onChange={(e) => setOrderQty(e.target.value)}
                sx={{ width: 140 }}
              />
              <TextField
                label="계획일"
                size="small"
                type="date"
                value={orderDate}
                onChange={(e) => setOrderDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              {editingOrderId ? (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUpdateOrder}
                    disabled={
                      !selectedItemId ||
                      selectedItemId === "ALL" ||
                      !orderQty ||
                      !orderDate
                    }
                  >
                    수정 저장
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={clearOrderInputs}
                  >
                    수정 취소
                  </Button>
                </>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddOrder}
                  disabled={
                    !selectedItemId ||
                    selectedItemId === "ALL" ||
                    !orderQty ||
                    !orderDate
                  }
                >
                  추가
                </Button>
              )}
            </Box>
          </Box>

          <Paper sx={{ p: 1, maxHeight: 260, overflow: "auto" }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {selectedItemId === "ALL"
                ? "전체 임시 계획 목록"
                : "선택된 품목의 임시 계획 목록"}
            </Typography>
            {orderLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 2,
                }}
              >
                <CircularProgress size={24} />
              </Box>
            ) : matchedOrders.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                임시 계획 데이터가 없습니다.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">품목 코드</TableCell>
                    <TableCell align="center">수량</TableCell>
                    <TableCell align="center">계획일</TableCell>
                    <TableCell align="center">수정</TableCell>
                    <TableCell align="center">삭제</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {matchedOrders.map((order) => (
                    <TableRow key={order.t_order_id} hover>
                      <TableCell align="center">
                        {getItemCodeById(order.item_id)}
                      </TableCell>
                      <TableCell align="center">{order.qty}</TableCell>
                      <TableCell align="center">{order.t_date}</TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleClickEditOrder(order)}
                        >
                          수정
                        </Button>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => handleDeleteOrder(order.t_order_id)}
                        >
                          삭제
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </S.ContentContainer>
      )}

      {/* 재고관리 탭 */}
      {tabValue === 1 && (
        <S.ContentContainer>
          <S.TopBtnBox>
            <Button
              variant="outlined"
              color="primary"
              onClick={stockInfoTestFunc}
            >
              Stock Info 생성(임시)
            </Button>

            <ToggleButtonGroup
              value={itemTypeFilter}
              exclusive
              onChange={handleItemTypeFilterChange}
              aria-label="품목 유형 필터"
              size="small"
              sx={{
                ml: 2,
                mr: 2,
                "& .MuiToggleButton-root": {
                  px: 2,
                  py: 0.75,
                  fontWeight: 600,
                  border: "1px solid rgba(0, 0, 0, 0.12)",
                  "&.Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                },
              }}
            >
              <ToggleButton value={1} aria-label="완제품">
                완제품
              </ToggleButton>
              <ToggleButton value={0} aria-label="반제품">
                반제품
              </ToggleButton>
            </ToggleButtonGroup>
            {editMode ? (
              <>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<RestartAltIcon />}
                  onClick={handleReset}
                  disabled={loading || saving}
                >
                  취소
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={
                    !isDataChanged() ||
                    !isValidStockValues() ||
                    loading ||
                    saving
                  }
                  sx={{ ml: 1 }}
                >
                  {saving ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "저장"
                  )}
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleToggleEditMode}
                disabled={loading}
              >
                수정
              </Button>
            )}
          </S.TopBtnBox>

          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress />
            </Box>
          ) : stockData.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Typography variant="body1" color="text.secondary">
                재고 데이터가 없습니다.
              </Typography>
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              sx={{ flex: 1, maxHeight: "100%", mt: 2 }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      자재 그룹
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      품목 코드
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      재고 수량
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      안전 재고
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      품목 유형
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredStockData.map((item, index) => {
                    const originalIndex = stockData.findIndex(
                      (data) => data.item_id === item.item_id
                    );
                    return (
                      <TableRow key={item.item_id || index} hover>
                        <TableCell align="center">
                          {item.material_grouping}
                        </TableCell>
                        <TableCell align="center">{item.item_code}</TableCell>
                        <TableCell align="center">
                          {editMode ? (
                            <TextField
                              type="number"
                              value={item.stock_quantity ?? ""}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === "" || /^-?\d*$/.test(value)) {
                                  handleValueChange(
                                    originalIndex,
                                    "stock_quantity",
                                    value === "" ? "" : parseInt(value, 10) || 0
                                  );
                                }
                              }}
                              onBlur={(e) => {
                                const value = e.target.value;
                                if (
                                  value !== "" &&
                                  !Number.isInteger(parseFloat(value))
                                ) {
                                  handleValueChange(
                                    originalIndex,
                                    "stock_quantity",
                                    Math.floor(parseFloat(value) || 0)
                                  );
                                }
                              }}
                              size="small"
                              inputProps={{
                                min: 0,
                                step: 1,
                                style: { textAlign: "center" },
                              }}
                              sx={{ width: "120px" }}
                              error={
                                item.stock_quantity !== null &&
                                item.stock_quantity !== undefined &&
                                !Number.isInteger(item.stock_quantity)
                              }
                              helperText={
                                item.stock_quantity !== null &&
                                item.stock_quantity !== undefined &&
                                !Number.isInteger(item.stock_quantity)
                                  ? "정수만 입력 가능합니다"
                                  : ""
                              }
                            />
                          ) : (
                            item.stock_quantity
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {editMode ? (
                            <TextField
                              type="number"
                              value={item.safety_stock ?? ""}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === "" || /^-?\d*$/.test(value)) {
                                  handleValueChange(
                                    originalIndex,
                                    "safety_stock",
                                    value === "" ? "" : parseInt(value, 10) || 0
                                  );
                                }
                              }}
                              onBlur={(e) => {
                                const value = e.target.value;
                                if (
                                  value !== "" &&
                                  !Number.isInteger(parseFloat(value))
                                ) {
                                  handleValueChange(
                                    originalIndex,
                                    "safety_stock",
                                    Math.floor(parseFloat(value) || 0)
                                  );
                                }
                              }}
                              size="small"
                              inputProps={{
                                min: 0,
                                step: 1,
                                style: { textAlign: "center" },
                              }}
                              sx={{ width: "120px" }}
                              error={
                                item.safety_stock !== null &&
                                item.safety_stock !== undefined &&
                                !Number.isInteger(item.safety_stock)
                              }
                              helperText={
                                item.safety_stock !== null &&
                                item.safety_stock !== undefined &&
                                !Number.isInteger(item.safety_stock)
                                  ? "정수만 입력 가능합니다"
                                  : ""
                              }
                            />
                          ) : (
                            item.safety_stock
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {getItemTypeLabel(item.item_type)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </S.ContentContainer>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </S.PageContainer>
  );
}

export default TempStock;
